import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.min.js';

// Patch every 2D canvas context globally so PDF.js's internal offscreen
// canvases (used for SMask compositing) also get willReadFrequently.
(function () {
    var orig = HTMLCanvasElement.prototype.getContext;
    HTMLCanvasElement.prototype.getContext = function (type, attrs) {
        if (type === '2d') attrs = Object.assign({ willReadFrequently: true }, attrs || {});
        return orig.call(this, type, attrs);
    };
}());

function renderPage(page, canvas, zoom) {
    var dpr            = window.devicePixelRatio || 1;
    var containerWidth = canvas.parentElement.clientWidth;
    var base           = page.getViewport({ scale: 1, rotation: page.rotate });
    var fitScale       = containerWidth / base.width;
    var renderScale    = fitScale * zoom * dpr;
    var viewport       = page.getViewport({ scale: renderScale, rotation: page.rotate });

    canvas.width  = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width       = Math.floor(base.width * fitScale * zoom) + 'px';
    canvas.style.aspectRatio = base.width + '/' + base.height;
    canvas.style.height      = '';

    return page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport });
}

function initViewer(container) {
    var canvas   = container.querySelector('[data-pdf-canvas]');
    var prevBtn  = container.querySelector('[data-pdf-prev]');
    var nextBtn  = container.querySelector('[data-pdf-next]');
    var pageInfo = container.querySelector('[data-pdf-page-info]');
    var zoomEl   = container.querySelector('[data-pdf-scale]');
    var loading  = container.querySelector('[data-pdf-loading]');
    var error    = container.querySelector('[data-pdf-error]');

    var currentPage = 1;
    var totalPages  = 0;
    var zoom        = parseFloat((zoomEl && zoomEl.value) || 2.0);
    var pdfDoc      = null;
    var currentTask = null;
    var renderSeq   = 0;

    // Pre-render cache — keyed by page number.
    // Each entry: { task, canvas, cssWidth, aspectRatio, zoom, containerWidth }
    var cache = {};

    function cacheKey(num) { return num; }

    function invalidateCache() {
        Object.keys(cache).forEach(function (k) {
            if (cache[k] && cache[k].task) cache[k].task.cancel();
        });
        cache = {};
    }

    function prerenderPageNum(num) {
        if (!pdfDoc || num < 1 || num > totalPages || cache[num]) return;

        var snapZoom          = zoom;
        var snapContainerWidth = canvas.parentElement.clientWidth;

        pdfDoc.getPage(num).then(function (page) {
            // Discard if conditions changed since we queued this
            if (zoom !== snapZoom ||
                canvas.parentElement.clientWidth !== snapContainerWidth ||
                cache[num]) return;

            var dpr        = window.devicePixelRatio || 1;
            var base       = page.getViewport({ scale: 1, rotation: page.rotate });
            var fitScale   = snapContainerWidth / base.width;
            var viewport   = page.getViewport({ scale: fitScale * snapZoom * dpr, rotation: page.rotate });
            var offscreen  = document.createElement('canvas');
            offscreen.width  = Math.floor(viewport.width);
            offscreen.height = Math.floor(viewport.height);

            var task = page.render({
                canvasContext: offscreen.getContext('2d'),
                viewport: viewport,
            });

            cache[num] = {
                task:          task,
                canvas:        offscreen,
                cssWidth:      Math.floor(base.width * fitScale * snapZoom),
                aspectRatio:   base.width + '/' + base.height,
                zoom:          snapZoom,
                containerWidth: snapContainerWidth,
            };

            task.promise.catch(function (err) {
                if (err && err.name !== 'RenderingCancelledException') delete cache[num];
            });
        });
    }

    function schedulePrerender(num) {
        prerenderPageNum(num + 1);
        prerenderPageNum(num - 1);
    }

    function updateControls() {
        if (pageInfo) pageInfo.textContent = 'Page ' + currentPage + ' of ' + totalPages;
        if (prevBtn)  prevBtn.disabled = currentPage <= 1;
        if (nextBtn)  nextBtn.disabled = currentPage >= totalPages;
    }

    function render(num) {
        if (currentTask) { currentTask.cancel(); currentTask = null; }

        var seq = ++renderSeq;
        currentPage = num;
        updateControls();

        var entry = cache[num];
        var isCacheValid = entry &&
            entry.zoom === zoom &&
            entry.containerWidth === canvas.parentElement.clientWidth;

        if (isCacheValid) {
            delete cache[num];

            // Page is (or will be) rendered in the offscreen canvas — wait for it then copy.
            entry.task.promise.then(function () {
                if (seq !== renderSeq) return;

                canvas.width             = entry.canvas.width;
                canvas.height            = entry.canvas.height;
                canvas.style.width       = entry.cssWidth + 'px';
                canvas.style.aspectRatio = entry.aspectRatio;
                canvas.style.height      = '';
                canvas.getContext('2d').drawImage(entry.canvas, 0, 0);

                schedulePrerender(num);
            }).catch(function (err) {
                if (err && err.name === 'RenderingCancelledException') return;
                // Cache miss on error — fall back to a fresh render
                if (seq === renderSeq) render(num);
            });
        } else {
            pdfDoc.getPage(num).then(function (page) {
                if (seq !== renderSeq) return;

                var task = renderPage(page, canvas, zoom);
                currentTask = task;

                task.promise.then(function () {
                    if (seq !== renderSeq) return;
                    currentTask = null;
                    schedulePrerender(num);
                }).catch(function (err) {
                    if (err && err.name === 'RenderingCancelledException') return;
                    console.error('[pdf-viewer] render error:', err);
                });
            });
        }
    }

    function loadSrc(source) {
        if (currentTask) { currentTask.cancel(); currentTask = null; }
        invalidateCache();
        ++renderSeq;

        if (loading) loading.style.display = '';
        if (error)   error.style.display   = 'none';
        if (canvas)  canvas.style.display  = 'none';

        pdfjsLib.getDocument(source).promise
            .then(function (doc) {
                pdfDoc      = doc;
                totalPages  = doc.numPages;
                currentPage = 1;
                if (loading) loading.style.display = 'none';
                if (canvas)  canvas.style.display  = '';
                render(currentPage);
            })
            .catch(function (err) {
                console.error('[pdf-viewer] load error:', err);
                if (loading) loading.style.display = 'none';
                if (error) {
                    error.textContent = 'Could not load PDF: ' + (err.message || err);
                    error.style.display = '';
                }
            });
    }

    if (prevBtn) {
        prevBtn.addEventListener('click', function () {
            if (pdfDoc && currentPage > 1) render(currentPage - 1);
        });
    }

    if (nextBtn) {
        nextBtn.addEventListener('click', function () {
            if (pdfDoc && currentPage < totalPages) render(currentPage + 1);
        });
    }

    if (zoomEl) {
        zoomEl.addEventListener('change', function () {
            zoom = parseFloat(this.value);
            invalidateCache();
            if (pdfDoc) render(currentPage);
        });
    }

    if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
        var resizeTimer = null;
        new ResizeObserver(function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(function () {
                if (pdfDoc) { invalidateCache(); render(currentPage); }
            }, 100);
        }).observe(canvas.parentElement);
    }

    container.addEventListener('pdf:load', function (e) {
        var src = e.detail && e.detail.src;
        if (!src) return;
        loadSrc(typeof src === 'string' ? { url: src } : src);
    });

    var initialSrc = container.getAttribute('data-pdf-src');
    if (initialSrc) {
        loadSrc({ url: initialSrc });
    } else {
        if (loading) loading.style.display = 'none';
    }
}

document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-pdf-viewer]').forEach(initViewer);
});
